import { parametrosDoObjeto } from './parametros'
import { paraLocal, pisosDaTorreEm, terrenoNoPonto } from './superficies'
import type { CenaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * O que a queda de um trabalhador atinge: o mesmo critério para a queda
 * animada (cálculo) e para a simulação com física. O corpo é um segmento
 * vertical, com três pontos (pés, meio e cabeça) sob o engate. O terreno é
 * sólido: entrar abaixo dele é bater. O piso de uma torre é fino: bate quem o
 * atravessa de cima para baixo entre um instante e o seguinte. Os montantes da
 * torre só entram quando pedidos (a física pede; a animação não): o corpo tem
 * largura, então bate num montante quando o eixo dele passa a menos de meia
 * largura da face do montante. As diagonais nunca bloqueiam.
 */

export const ALTURA_DA_CABECA_M = 1.75
export const TOLERANCIA_TERRENO_M = 0.02
/** Largura do corpo, para bater nos montantes da torre: 36 cm de ombro a ombro. */
export const LARGURA_DO_CORPO_M = 0.36

export type ParteDoCorpo = 'pes' | 'corpo' | 'cabeca'

export interface CorpoDaQueda {
  readonly engateAosPesM: number
}

export interface PontoDeImpacto {
  readonly parte: ParteDoCorpo
  readonly ponto: VetorTabletop
  /** Nome da peça atingida: o terreno, o patamar ou o montante da torre. */
  readonly alvo: string
}

export function pontosDoCorpo(engate: VetorTabletop, corpo: CorpoDaQueda): ReadonlyArray<readonly [ParteDoCorpo, VetorTabletop]> {
  const yPes = engate[1] - corpo.engateAosPesM
  return [
    ['cabeca', [engate[0], yPes + ALTURA_DA_CABECA_M, engate[2]]],
    ['corpo', [engate[0], yPes + ALTURA_DA_CABECA_M / 2, engate[2]]],
    ['pes', [engate[0], yPes, engate[2]]],
  ]
}

/** A espessura dos montantes da torre, a mesma com que ela é desenhada. */
export function espessuraDoMontanteM(larguraM: number): number {
  return Math.max(0.05, Math.min(0.12, larguraM * 0.03))
}

/** O ponto (x, y, z) está dentro de um montante da torre, engrossado pela meia largura do corpo. */
function dentroDeMontante(torre: ObjetoTabletop, ponto: VetorTabletop): boolean {
  const { alturaM, larguraM } = parametrosDoObjeto(torre)
  const { escala, posicao } = torre.transformacao
  const base = posicao[1]
  if (ponto[1] < base || ponto[1] > base + alturaM! * escala[1]) return false
  const { lx, lz } = paraLocal(torre, ponto[0], ponto[2])
  const meio = larguraM! / 2
  const metade = espessuraDoMontanteM(larguraM!) / 2
  const folgaX = metade + LARGURA_DO_CORPO_M / 2 / escala[0]
  const folgaZ = metade + LARGURA_DO_CORPO_M / 2 / escala[2]
  return Math.abs(Math.abs(lx) - meio) <= folgaX && Math.abs(Math.abs(lz) - meio) <= folgaZ
}

export interface OpcoesDetectorDeImpacto {
  /** Os montantes da torre bloqueiam o corpo. */
  readonly montantes?: boolean
}

export type DetectorDeImpacto = (anterior: VetorTabletop | null, engate: VetorTabletop, corpo: CorpoDaQueda) => PontoDeImpacto | null

/**
 * O detector de impacto da cena: dado o engate no instante anterior (se
 * houver) e no atual, devolve a primeira parte do corpo que bateu, ou `null`.
 */
export function criarDetectorDeImpacto(cena: CenaTabletop, opcoes: OpcoesDetectorDeImpacto = {}): DetectorDeImpacto {
  const torres = cena.objetos.filter((o) => o.tipo === 'torre' && !o.oculto)
  return (anterior, engate, corpo) => {
    const antes = anterior ? pontosDoCorpo(anterior, corpo) : null
    for (const [i, [parte, ponto]] of pontosDoCorpo(engate, corpo).entries()) {
      const terreno = terrenoNoPonto(cena, ponto[0], ponto[2])
      if (ponto[1] < terreno.altura - TOLERANCIA_TERRENO_M) return { parte, ponto, alvo: terreno.nome }
      if (antes) {
        const yAntes = antes[i]![1][1]
        for (const torre of torres) {
          for (const piso of pisosDaTorreEm(torre, ponto[0], ponto[2])) {
            if (yAntes >= piso.altura - TOLERANCIA_TERRENO_M && ponto[1] < piso.altura - TOLERANCIA_TERRENO_M) {
              return { parte, ponto: [ponto[0], piso.altura, ponto[2]], alvo: piso.nome }
            }
          }
        }
      }
      if (opcoes.montantes) {
        for (const torre of torres) {
          if (dentroDeMontante(torre, ponto)) return { parte, ponto, alvo: `um montante da ${torre.nome}` }
        }
      }
    }
    return null
  }
}
